import { SystemTypes } from "spark-core-dx/services";
import {
  applySetupFormFieldOverrides,
  asArray,
  buildSetupFormMetadata,
  getDisplayName,
  getFirst,
  getId,
  getLinkedRecord,
  getLinkedRecordItemId,
  getMetadataFields,
  getOperationItem,
  getOperationItems,
  getProductResourceDefaults,
  getRunSectionStepInputValues,
  getSetupFormFieldOverrides,
  getSetupFormFieldStateOverrides,
  keepObjectStateWhenEqual,
  readValue,
  requireSuccessfulServiceResponse,
  validateRequiredCardGridRows,
  validateRequiredIntegrationFields
} from "../resourceConfigWizardUtils";

export const DESTINATION_RELATIONSHIP = "SchemaDestination";
export const DESTINATION_FORM_GROUP_ID = "resourceWizardDestinationV1";
export const DESTINATION_DEPLOYMENT_FORM_GROUP_ID = "resourceWizardDestinationDeploymentV1";
export const DESTINATION_ENTITY_DEPLOYMENT_FORM_GROUP_ID = "resourceWizardDestinationEntityDeploymentV1";

export const DESTINATION_KIND_WAREHOUSE = "Warehouse";
export const DESTINATION_KIND_FILE_STORAGE = "FileStorage";
export const DESTINATION_KIND_EXTERNAL_SYSTEM = "ExternalSystem";
export const DESTINATION_KIND_SCHEMA_API = "SchemaAPI";
export const DESTINATION_SYSTEM_TYPE_FTP_FILE_STORAGE = 13;

const DESTINATION_STEP_KEY = "destination";
const DEPLOYMENT_STEP_KEY = "deployment";
const ENTITY_DEPLOYMENTS_STEP_KEY = "entity-deployments";
const OUTBOUND_EDI_ENTITY_OBJECTS_STEP_KEY = "edi-entity-objects";
const CONFIGURE_STEP_KEY = "configure";

const DESTINATION_PARENT_HIDDEN_FIELD_NAMES = [
  "destinationsystemtype",
  "systemname",
  "isactive",
  "isnew",
  "nextschemaversionid",
  "entityobjectdeployments",
  "deployments",
  "warehousedeployments",
  "storagedeployments",
  "storagedeploymentsalias",
  "externalsystemdeployments",
  "views"
];

const ENDPOINT_PARENT_HIDDEN_FIELD_NAMES = [
  "destinationsystemtype",
  "systemname",
  "isactive",
  "isnew",
  "nextschemaversionid",
  "entityobjectdeployments",
  "deployments",
  "apideployments",
  "warehousedeployments",
  "storagedeployments",
  "storagedeploymentsalias",
  "externalsystemdeployments",
  "defaultconnectionstring",
  "defaultdbname",
  "defaultrootpath",
  "defaulttargetname",
  "enablesync",
  "enablepreview",
  "views"
];

const DESTINATION_DEPLOYMENT_HIDDEN_FIELD_NAMES = [
  "destinationsystemtype",
  "systemname",
  "schemadestinationstatus",
  "currentversion",
  "nextversion",
  "currentschemaversion",
  "nextschemaversion",
  "nextschemaversionid",
  "upgradeRequired",
  "lastsyncstartdate",
  "lastsyncenddate",
  "lasterrors",
  "initialonlinedate",
  "onlinedate",
  "offlinedate",
  "laststatuscheck"
];

const ENDPOINT_DEPLOYMENT_HIDDEN_FIELD_NAMES = [
  "destinationsystemtype",
  "systemname",
  "schemadestinationstatus",
  "currentversion",
  "nextversion",
  "currentschemaversion",
  "nextschemaversion",
  "nextschemaversionid",
  "upgraderequired",
  "lastsyncstartdate",
  "lastsyncenddate",
  "lasterrors",
  "initialonlinedate",
  "onlinedate",
  "offlinedate",
  "laststatuscheck",
  "entityendpoints",
  "modelendpoints",
  "apitokens",
  "batchhostregistrationid",
  "batchpublicbaseurl"
];

const ENDPOINT_DEPLOYMENT_SETUP_HIDDEN_TAB_KEYS = new Set(["auth", "batch", "general"]);
const ENDPOINT_DEPLOYMENT_SETUP_BATCH_FIELD_ALLOWLIST = new Set([
  "runtimecapacityclientproductofferid",
  "batchpoolprofileid"
]);
const ENDPOINT_AUTH_MODE_PAT_TOKEN = 1;
const ENDPOINT_AUTH_MODE_OAUTH2_USER_FLOW = 6;
const ENDPOINT_MCP_AUTH_MODE_OPTIONS = [{ Text: "OAuth 2 User Flow", Value: "6" }];
const ENDPOINT_MCP_IDENTITY_MODE_SERVICE_ACCOUNT = 1;
const ENDPOINT_MCP_IDENTITY_MODE_USER_CONTEXT = 2;

const ENTITY_DEPLOYMENT_HIDDEN_FIELD_NAMES = [
  "lastprevioussyncstartdate",
  "lastsyncstartdate",
  "lastsyncenddate",
  "lasterrors"
];

const DESTINATION_STATE_OPTIONS = [
  { Text: "Normal", Value: "1" },
  { Text: "Creating", Value: "2" },
  { Text: "Deploying", Value: "3" },
  { Text: "Updating", Value: "4" },
  { Text: "Partial Sync In Progress", Value: "5" },
  { Text: "Full Sync In Progress", Value: "6" },
  { Text: "Validating", Value: "7" },
  { Text: "Previewing In Progress", Value: "8" },
  { Text: "Deleting", Value: "90" },
  { Text: "Error", Value: "99" },
  { Text: "Unknown", Value: "0" }
];

const FILE_PACKAGING_OPTIONS = [
  { Text: "Batch", Value: "1" },
  { Text: "Per Document", Value: "2" }
];

const normalizeText = (value) => String(value ?? "").trim().toLowerCase();
const lowerFirst = (value = "") => value ? `${value.charAt(0).toLowerCase()}${value.slice(1)}` : "";
const hasOwn = (item, key) => Object.prototype.hasOwnProperty.call(item || {}, key);

const DESTINATION_DOWNSTREAM_STATUS_FIELD_NAMES = [
  "DestinationConnectionCheckStatus",
  "DestinationConnectionCheckMessage",
  "DestinationConnectionCheckedUtc",
  "DestinationConnectionCheckDeploymentId",
  "DestinationConnectionCheckDeploymentName",
  "ConnectionCheckStatus",
  "ConnectionCheckMessage",
  "ConnectionCheckedUtc",
  "ConnectionCheckDeploymentId",
  "ConnectionCheckDeploymentName"
];

export const normalizeDestinationKind = (value) => {
  const normalized = String(value || "").replace(/[^a-z0-9]+/gi, "").toLowerCase();
  if (normalized === "warehouse") return DESTINATION_KIND_WAREHOUSE;
  if (normalized === "filestorage" || normalized === "storage") return DESTINATION_KIND_FILE_STORAGE;
  if (normalized === "externalsystem" || normalized === "externalsystemsync") return DESTINATION_KIND_EXTERNAL_SYSTEM;
  if (normalized === "schemaapi" || normalized === "api" || normalized === "endpoint") return DESTINATION_KIND_SCHEMA_API;
  return String(value || "");
};

export const isSchemaAPISetup = (defaults = {}) =>
  getDestinationKind(defaults) === DESTINATION_KIND_SCHEMA_API;

export const getDeliveryRouteOptions = (defaults = {}) =>
  asArray(readValue(defaults, "deliveryRouteOptions", "DeliveryRouteOptions"))
    .map((optionItem) => {
      const value = readValue(optionItem, "Value", "value", "deliveryProductResourceKey", "DeliveryProductResourceKey", "productResourceKey", "ProductResourceKey");
      return {
        ...(optionItem || {}),
        Text: readValue(optionItem, "Text", "text", "DisplayName", "displayName") || value,
        Value: value
      };
    })
    .filter((optionItem) => optionItem.Value);

export const hasDeliveryRouteOptions = (defaults = {}) =>
  getDeliveryRouteOptions(defaults).length > 0;

export const getSelectedDeliveryProductResourceKey = (wizard, values = {}, defaults = {}) =>
  readValue(values, "DeliveryProductResourceKey", "deliveryProductResourceKey")
  || readValue(getSavedDestinationStepValues(wizard), "DeliveryProductResourceKey", "deliveryProductResourceKey")
  || readValue(defaults, "defaultDeliveryProductResourceKey", "DefaultDeliveryProductResourceKey")
  || "";

export const getSelectedDeliveryRouteOption = (wizard, values = {}, defaults = {}) => {
  const selectedKey = getSelectedDeliveryProductResourceKey(wizard, values, defaults);
  if (!selectedKey) return null;
  return getDeliveryRouteOptions(defaults).find((optionItem) =>
    normalizeText(optionItem.Value) === normalizeText(selectedKey)
    || normalizeText(readValue(optionItem, "deliveryProductResourceKey", "DeliveryProductResourceKey")) === normalizeText(selectedKey)
    || normalizeText(readValue(optionItem, "productResourceKey", "ProductResourceKey")) === normalizeText(selectedKey)
  ) || null;
};

export const resolveDeliveryRouteDefaults = (wizard, defaults = {}, values = {}) => {
  const routeOptions = getDeliveryRouteOptions(defaults);
  if (!routeOptions.length) return defaults || {};

  const selectedRoute = getSelectedDeliveryRouteOption(wizard, values, defaults);
  if (!selectedRoute) {
    return {
      ...(defaults || {}),
      deliveryRouteOptions: routeOptions,
      destinationStepTitle: readValue(defaults, "destinationStepTitle", "DestinationStepTitle") || "File Destination",
      destinationStepDescription: readValue(defaults, "destinationStepDescription", "DestinationStepDescription")
        || "Choose the file destination type, then configure the destination connection."
    };
  }

  const selectedKey = readValue(selectedRoute, "Value", "value", "deliveryProductResourceKey", "DeliveryProductResourceKey", "productResourceKey", "ProductResourceKey");
  return {
    ...(defaults || {}),
    ...selectedRoute,
    deliveryRouteOptions: routeOptions,
    DeliveryProductResourceKey: selectedKey,
    deliveryProductResourceKey: selectedKey,
    selectedDeliveryProductResourceKey: selectedKey
  };
};

export const getDestinationSetupDefaults = (wizard, contextDefaults = null, values = {}) =>
  resolveDeliveryRouteDefaults(wizard, contextDefaults || getProductResourceDefaults(wizard), values);

export const getDestinationItemName = (defaults = {}) =>
  readValue(defaults, "destinationItemName", "DestinationItemName") || "SchemaDestination";

export const getDestinationRelationship = (defaults = {}) =>
  readValue(defaults, "destinationRelationship", "DestinationRelationship") || DESTINATION_RELATIONSHIP;

export const getDestinationKind = (defaults = {}) =>
  normalizeDestinationKind(readValue(defaults, "destinationKind", "DestinationKind") || getDestinationItemName(defaults));

export const getDestinationLabel = (defaults = {}) =>
  readValue(defaults, "destinationLabel", "DestinationLabel") || getDestinationItemName(defaults) || "Destination";

export const getDestinationStepTitle = (defaults = {}) =>
  readValue(defaults, "destinationStepTitle", "DestinationStepTitle", "providerName", "ProviderName")
  || getDestinationLabel(defaults);

export const getDestinationStepDescription = (defaults = {}) =>
  readValue(defaults, "destinationStepDescription", "DestinationStepDescription")
  || `Configure one ${getDestinationStepTitle(defaults)} owned by this Resource Configuration.`;

export const getDestinationDeploymentStepDescription = (defaults = {}) =>
  `Add deployment rows for this ${getDestinationStepTitle(defaults) || getDestinationLabel(defaults)}.`;

export const getDeploymentLabel = (defaults = {}) =>
  readValue(defaults, "deploymentLabel", "DeploymentLabel") || readValue(defaults, "deploymentItemName", "DeploymentItemName") || "Deployment";

export const getDestinationManagerKey = (defaults = {}) => {
  const itemName = normalizeText(getDestinationItemName(defaults));
  if (itemName === "warehouse") return "warehouse";
  if (itemName === "filestorage") return "fileStorage";
  if (itemName === "externalsystem") return "externalSystem";
  if (itemName === "schemaapi") return "schemaAPI";
  return itemName;
};

export const getDestinationSystemTypeFieldName = (defaults = {}) =>
  readValue(defaults, "systemTypeFieldName", "SystemTypeFieldName") || "DestinationSystemType";

export const getDestinationSystemTypeValue = (defaults = {}) => {
  const value = readValue(defaults, "destinationSystemTypeValue", "DestinationSystemTypeValue");
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : value;
};

export const getDestinationSystemTypeKey = (defaults = {}) =>
  readValue(defaults, "destinationSystemTypeKey", "DestinationSystemTypeKey") || "";

export const getDeploymentCollectionField = (defaults = {}) =>
  readValue(defaults, "deploymentCollectionField", "DeploymentCollectionField") || "Deployments";

export const getDeploymentItemName = (defaults = {}) =>
  readValue(defaults, "deploymentItemName", "DeploymentItemName") || "SchemaDestinationDeployment";

export const getDestinationCheckOperationName = (defaults = {}) =>
  readValue(defaults, "checkOperationName", "CheckOperationName") || "";

export const isRequestOnlyDestinationSetup = (defaults = {}) =>
  readValue(defaults, "requestOnly", "RequestOnly") === true
  || String(readValue(defaults, "requestOnly", "RequestOnly") || "").toLowerCase() === "true";

export const getDestinationRequestOnlyReason = (defaults = {}) =>
  readValue(defaults, "requestOnlyReason", "RequestOnlyReason")
  || `${getDestinationStepTitle(defaults)} requires Convoke administrator setup.`;

export const isCoreProfileMetadataReady = (coreContext) =>
  Boolean(coreContext?.Profile?.ClientId && coreContext?.Profile?.AppId);

export const hasLoadedManagerMetadata = (manager) =>
  Array.isArray(manager?.uiMetadata);

export const loadDestinationManagerMetadata = async ({ manager, coreContext } = {}) => {
  if (hasLoadedManagerMetadata(manager)) {
    return {
      Success: true,
      Items: manager.uiMetadata,
      Cached: true
    };
  }
  if (manager?.GetMetadata) {
    const profile = coreContext?.Profile || {};
    return manager.GetMetadata({
      clientId: profile.ClientId,
      appId: profile.AppId,
      forceReload: true
    });
  }
  return {
    Success: true,
    Items: manager?.uiMetadata || []
  };
};

export const getParentTargetFieldName = (defaults = {}) => {
  switch (getDestinationKind(defaults)) {
    case DESTINATION_KIND_WAREHOUSE:
      return "DefaultDBName";
    case DESTINATION_KIND_FILE_STORAGE:
      return "DefaultRootPath";
    case DESTINATION_KIND_EXTERNAL_SYSTEM:
      return "DefaultTargetName";
    case DESTINATION_KIND_SCHEMA_API:
      return "APIName";
    default:
      return "SystemName";
  }
};

export const getDeploymentTargetFieldName = (defaults = {}) => {
  switch (getDestinationKind(defaults)) {
    case DESTINATION_KIND_WAREHOUSE:
      return "DBName";
    case DESTINATION_KIND_FILE_STORAGE:
      return "RootPath";
    case DESTINATION_KIND_EXTERNAL_SYSTEM:
      return "TargetName";
    case DESTINATION_KIND_SCHEMA_API:
      return "Key";
    default:
      return "SystemName";
  }
};

export const getDeploymentStateFieldName = (defaults = {}) => {
  switch (getDestinationKind(defaults)) {
    case DESTINATION_KIND_WAREHOUSE:
      return "WarehouseState";
    case DESTINATION_KIND_FILE_STORAGE:
      return "StorageState";
    case DESTINATION_KIND_EXTERNAL_SYSTEM:
      return "ExternalSystemState";
    default:
      return "";
  }
};

export const getSavedDestinationStepValues = (wizard, values = {}) => ({
  ...getRunSectionStepInputValues(wizard, CONFIGURE_STEP_KEY, DESTINATION_STEP_KEY),
  ...values
});

export const getSavedDeploymentStepValues = (wizard, values = {}) => ({
  ...getRunSectionStepInputValues(wizard, CONFIGURE_STEP_KEY, DEPLOYMENT_STEP_KEY),
  ...values
});

export const getSavedEntityDeploymentStepValues = (wizard, values = {}) => ({
  ...getRunSectionStepInputValues(wizard, CONFIGURE_STEP_KEY, ENTITY_DEPLOYMENTS_STEP_KEY),
  ...values
});

export const getLinkedDestinationRecord = (wizard, defaults = {}) =>
  getLinkedRecord(wizard, getDestinationItemName(defaults), getDestinationRelationship(defaults))
  || getLinkedRecord(wizard, getDestinationItemName(defaults), DESTINATION_RELATIONSHIP)
  || null;

const getInheritedDestinationSchemaValues = (wizard) => {
  const entityObjectValues = getRunSectionStepInputValues(wizard, CONFIGURE_STEP_KEY, OUTBOUND_EDI_ENTITY_OBJECTS_STEP_KEY);
  return {
    schemaId: readValue(entityObjectValues, "SchemaId", "schemaId")
      || getLinkedRecordItemId(getLinkedRecord(wizard, "Schema", "EntitySchema"))
      || "",
    activeSchemaVersionId: readValue(entityObjectValues, "ActiveSchemaVersionId", "activeSchemaVersionId") || ""
  };
};

export const getDestinationSchemaContext = (wizard, values = {}) => {
  const savedValues = getSavedDestinationStepValues(wizard, values);
  const inheritedSchemaValues = getInheritedDestinationSchemaValues(wizard);
  return {
    schemaId: readValue(savedValues, "SchemaId", "schemaId") || inheritedSchemaValues.schemaId || "",
    activeSchemaVersionId: readValue(savedValues, "ActiveSchemaVersionId", "activeSchemaVersionId") || inheritedSchemaValues.activeSchemaVersionId || ""
  };
};

export const getDestinationIdFromValues = (wizard, values = {}, defaults = {}) => {
  const savedValues = getSavedDestinationStepValues(wizard);
  const currentDeliveryKey = readValue(values, "DeliveryProductResourceKey", "deliveryProductResourceKey");
  const savedDeliveryKey = readValue(savedValues, "DeliveryProductResourceKey", "deliveryProductResourceKey");
  if (currentDeliveryKey && savedDeliveryKey && normalizeText(currentDeliveryKey) !== normalizeText(savedDeliveryKey)) {
    return readValue(values, "DestinationId", "destinationId") || "";
  }

  return readValue(values, "DestinationId", "destinationId")
    || readValue(savedValues, "DestinationId", "destinationId")
    || getLinkedRecordItemId(getLinkedDestinationRecord(wizard, defaults))
    || "";
};

export const toSafeSystemName = (value, fallback = "Destination") => {
  const fallbackValue = String(fallback || "Destination").trim() || "Destination";
  const normalized = String(value || "")
    .trim()
    .replace(/\s+[-_]+\s*|\s*[-_]+\s+/g, "_")
    .replace(/[^A-Za-z0-9_-]+/g, "_")
    .replace(/[_-]{2,}/g, (separator) => (
      separator.includes("_") && separator.includes("-")
        ? "_"
        : separator.charAt(0)
    ))
    .replace(/^[_-]+|[_-]+$/g, "");
  if (!normalized) return fallbackValue;
  return /^[A-Za-z]/.test(normalized) ? normalized : `${fallbackValue}_${normalized}`;
};

export const isFtpFileStorageDestination = (defaults = {}) =>
  getDestinationKind(defaults) === DESTINATION_KIND_FILE_STORAGE
  && Number(getDestinationSystemTypeValue(defaults)) === DESTINATION_SYSTEM_TYPE_FTP_FILE_STORAGE;

export const getGeneratedTargetValue = (name, defaults = {}) =>
  toSafeSystemName(name, getDestinationKind(defaults) || "Destination");

export const getDestinationDefaultTargetValue = (name, defaults = {}) =>
  isFtpFileStorageDestination(defaults)
    ? "/"
    : getGeneratedTargetValue(name, defaults);

export const normalizeDestinationTargetValue = (value, name, defaults = {}) => {
  const currentValue = String(value || "").trim();
  if (!isFtpFileStorageDestination(defaults)) return currentValue;
  if (!currentValue || currentValue === getGeneratedTargetValue(name, defaults)) return "/";
  return currentValue;
};

export const resolveDestinationTargetValue = (destination, name, defaults = {}) => {
  const parentTargetField = getParentTargetFieldName(defaults);
  const rawValue = readValue(
    destination,
    parentTargetField,
    parentTargetField.charAt(0).toLowerCase() + parentTargetField.slice(1),
    "SystemName",
    "systemName"
  );
  return normalizeDestinationTargetValue(rawValue, name, defaults)
    || getDestinationDefaultTargetValue(name, defaults);
};

export const createClientGuid = () => {
  const randomUuid = globalThis.crypto?.randomUUID?.();
  if (randomUuid) return randomUuid;
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (token) => {
    const value = Math.floor(Math.random() * 16);
    return (token === "x" ? value : (value & 0x3) | 0x8).toString(16);
  });
};

const option = (text, value = text) => ({ Text: text, Value: value });

export const getResourceDetailDestinationDefaults = (wizard) => {
  const details = getRunSectionStepInputValues(wizard, "basics", "resource-details");
  const configuration = readValue(wizard, "ResourceConfiguration", "resourceConfiguration") || {};
  return {
    name: readValue(details, "DisplayName", "displayName", "ConfigurationName", "configurationName")
      || getDisplayName(configuration)
      || readValue(wizard, "ResourceName", "resourceName")
      || "Destination",
    description: readValue(details, "Description", "description", "Notes", "notes")
      ?? readValue(configuration, "Description", "description", "Notes", "notes")
      ?? ""
  };
};

export const getClientId = (wizard, coreContext) => {
  const configuration = readValue(wizard, "ResourceConfiguration", "resourceConfiguration") || {};
  return readValue(configuration, "ClientId", "clientId")
    || readValue(coreContext, "Profile.ClientId")
    || coreContext?.Profile?.ClientId
    || "";
};

export const buildDestinationDraft = ({ wizard, values = {}, defaults = {}, coreContext }) => {
  const savedValues = getSavedDestinationStepValues(wizard, values);
  const detailDefaults = getResourceDetailDestinationDefaults(wizard);
  const name = readValue(savedValues, "Name", "name", "DestinationName", "destinationName")
    || detailDefaults.name;
  const description = readValue(savedValues, "Description", "description", "DestinationDescription", "destinationDescription")
    ?? detailDefaults.description;
  const parentTargetField = getParentTargetFieldName(defaults);
  const systemName = resolveDestinationTargetValue(savedValues, name, defaults);
  const systemTypeFieldName = getDestinationSystemTypeFieldName(defaults);
  const systemTypeValue = getDestinationSystemTypeValue(defaults);
  const schemaAPISetup = isSchemaAPISetup(defaults);
  const schemaContext = getDestinationSchemaContext(wizard, savedValues);

  return {
    ...savedValues,
    IsNew: !getId(savedValues),
    IsActive: true,
    IsDeleted: false,
    ClientId: getClientId(wizard, coreContext) || readValue(savedValues, "ClientId", "clientId") || "",
    DestinationSystemType: systemTypeValue,
    [systemTypeFieldName]: systemTypeValue,
    Name: name,
    Description: description,
    SystemName: systemName,
    [parentTargetField]: systemName,
    SchemaId: schemaContext.schemaId,
    ActiveSchemaVersionId: schemaContext.activeSchemaVersionId,
    ...(schemaAPISetup
      ? { DefaultAPIBasicAuthScope: readValue(savedValues, "DefaultAPIBasicAuthScope", "defaultAPIBasicAuthScope") || "" }
      : { EnableSync: readValue(savedValues, "EnableSync", "enableSync") === true }),
    ...(getDestinationKind(defaults) === DESTINATION_KIND_EXTERNAL_SYSTEM
      ? { EnablePreview: readValue(savedValues, "EnablePreview", "enablePreview") !== false }
      : {})
  };
};

export const ensureDestinationDefaults = (destination = {}, { wizard, defaults = {}, coreContext } = {}) => {
  const seed = buildDestinationDraft({ wizard, values: destination, defaults, coreContext });
  const parentTargetField = getParentTargetFieldName(defaults);
  const systemTypeFieldName = getDestinationSystemTypeFieldName(defaults);
  const systemTypeValue = getDestinationSystemTypeValue(defaults);
  const name = readValue(destination, "Name", "name") || seed.Name;
  const systemName = resolveDestinationTargetValue(destination, name, defaults);
  const schemaAPISetup = isSchemaAPISetup(defaults);
  return {
    ...seed,
    ...destination,
    IsNew: !getId(destination),
    IsActive: true,
    IsDeleted: false,
    ClientId: readValue(destination, "ClientId", "clientId") || seed.ClientId,
    DestinationSystemType: systemTypeValue,
    [systemTypeFieldName]: systemTypeValue,
    Name: name,
    Description: readValue(destination, "Description", "description") ?? seed.Description,
    SystemName: systemName,
    [parentTargetField]: systemName,
    ...(schemaAPISetup
      ? { DefaultAPIBasicAuthScope: readValue(destination, "DefaultAPIBasicAuthScope", "defaultAPIBasicAuthScope") || seed.DefaultAPIBasicAuthScope || "" }
      : {})
  };
};

export const resolveMetadataItem = (metadataSource, itemName) => {
  const normalizedName = normalizeText(itemName);
  const items = [
    ...getOperationItems(metadataSource),
    ...asArray(metadataSource)
  ].filter(Boolean);
  return items.find((item) =>
    normalizeText(readValue(item, "Name", "name", "ItemName", "itemName")) === normalizedName
  ) || getFirst(items) || getFirst(metadataSource?.Items) || getFirst(metadataSource) || null;
};

export const buildDestinationFormMetadata = (metadata, defaults = {}, { lockSchema = true } = {}) => {
  const setupMetadata = applySetupFormFieldOverrides(metadata, getSetupFormFieldOverrides(defaults, getDestinationItemName(defaults)));
  const fieldStates = getSetupFormFieldStateOverrides(defaults, getDestinationItemName(defaults));
  return buildSetupFormMetadata(setupMetadata, {
    hiddenFieldNames: [
      ...(isSchemaAPISetup(defaults) ? ENDPOINT_PARENT_HIDDEN_FIELD_NAMES : DESTINATION_PARENT_HIDDEN_FIELD_NAMES),
      normalizeText(getDestinationSystemTypeFieldName(defaults)),
      normalizeText(getDeploymentCollectionField(defaults)),
      fieldStates.IsHidden
    ],
    readOnlyFieldNames: [
      ...(lockSchema ? ["schemaid", "activeschemaversionid"] : []),
      fieldStates.IsReadOnly
    ]
  });
};

const isMcpOnlyEndpointSetup = (defaults = {}) =>
  isSchemaAPISetup(defaults)
  && (Number(readValue(defaults, "defaultPublicSurfaceModeValue", "DefaultPublicSurfaceModeValue")) === 2
    || normalizeText(readValue(defaults, "defaultPublicSurfaceModeKey", "DefaultPublicSurfaceModeKey")) === "mcponly");

const getSetupTabKey = (field) => {
  const rawTabId = readValue(field, "TabId", "tabId");
  if (rawTabId === undefined || rawTabId === null) return "";
  return normalizeText(String(rawTabId).split("|")[0]);
};

const hasExplicitSetupTab = (field) => hasOwn(field, "TabId") || hasOwn(field, "tabId");

const isEndpointDeploymentSetupTabHidden = (field) => {
  const fieldName = normalizeText(readValue(field, "FieldName", "fieldName"));
  if (ENDPOINT_DEPLOYMENT_SETUP_BATCH_FIELD_ALLOWLIST.has(fieldName)) return false;

  const tabKey = getSetupTabKey(field);
  if (!tabKey) return hasExplicitSetupTab(field);
  return ENDPOINT_DEPLOYMENT_SETUP_HIDDEN_TAB_KEYS.has(tabKey);
};

const filterEndpointDeploymentSetupTabs = (metadata) => {
  if (!metadata) return metadata;
  return {
    ...metadata,
    Fields: asArray(readValue(metadata, "Fields", "fields")).filter((field) => !isEndpointDeploymentSetupTabHidden(field))
  };
};

const applyEndpointDeploymentSetupDefaults = (metadata, defaults = {}) => {
  if (!metadata || !isMcpOnlyEndpointSetup(defaults)) return metadata;
  return {
    ...metadata,
    Fields: asArray(readValue(metadata, "Fields", "fields")).map((field) => {
      if (normalizeText(readValue(field, "FieldName", "fieldName")) !== "apiauthenticationmode") return field;
      return {
        ...field,
        DataJson: ENDPOINT_MCP_AUTH_MODE_OPTIONS,
        IsReadOnly: true
      };
    })
  };
};

export const buildDeploymentFormMetadata = (metadata, defaults = {}) => {
  const setupMetadata = applySetupFormFieldOverrides(metadata, getSetupFormFieldOverrides(defaults, getDeploymentItemName(defaults)));
  const fieldStates = getSetupFormFieldStateOverrides(defaults, getDeploymentItemName(defaults));
  const formMetadata = applyEndpointDeploymentSetupDefaults(buildSetupFormMetadata(setupMetadata, {
    hiddenFieldNames: [
      ...(isSchemaAPISetup(defaults) ? ENDPOINT_DEPLOYMENT_HIDDEN_FIELD_NAMES : DESTINATION_DEPLOYMENT_HIDDEN_FIELD_NAMES),
      ...(isMcpOnlyEndpointSetup(defaults) ? ["apiauthenticationmode"] : []),
      normalizeText(getDestinationSystemTypeFieldName(defaults)),
      fieldStates.IsHidden
    ],
    readOnlyFieldNames: fieldStates.IsReadOnly
  }), defaults);
  return isSchemaAPISetup(defaults) ? filterEndpointDeploymentSetupTabs(formMetadata) : formMetadata;
};

export const buildEntityDeploymentFormMetadata = ({
  metadata,
  entityOptions,
  deploymentOptions
}) => {
  const base = buildSetupFormMetadata(metadata, { hiddenFieldNames: ENTITY_DEPLOYMENT_HIDDEN_FIELD_NAMES });
  const fields = getMetadataFields(base);
  const fieldByName = new Map(fields.map((field) => [normalizeText(readValue(field, "FieldName", "fieldName")), field]));
  const field = (fieldName, displayName, fieldType, dataJson, isRequired = false, showOnGrid = true, sequence = 1, tabId = "Configuration|1", description = "") => ({
    ...(fieldByName.get(normalizeText(fieldName)) || {}),
    FieldName: fieldName,
    DisplayName: displayName,
    Description: description || fieldByName.get(normalizeText(fieldName))?.Description || "",
    FieldType: fieldType,
    DataJson: dataJson,
    IsRequired: isRequired,
    ShowOnGrid: showOnGrid,
    TabId: tabId,
    DisplaySequence: sequence,
    GridSequence: sequence
  });

  const resolvedFields = [
    field("EntityObjectId", "Entity Object", SystemTypes.UIFieldType.SearchSelectList, entityOptions, true, false, 1),
    field("SchemaDestinationDeploymentId", "Deployment", SystemTypes.UIFieldType.SearchSelectList, deploymentOptions, true, false, 2),
    field("EntityDisplayName", "Entity Object Name", SystemTypes.UIFieldType.Label, [], false, true, 3),
    field("SchemaDestinationName", "Schema Destination", SystemTypes.UIFieldType.Label, [], false, true, 4),
    field("FilePackagingMode", "File Packaging Mode", SystemTypes.UIFieldType.SelectList, FILE_PACKAGING_OPTIONS, false, false, 5),
    field("DisableTruncate", "Disable Truncate", SystemTypes.UIFieldType.CheckBox, [], false, false, 6, "Configuration|1", "When enabled, full loads will not clear existing destination data before loading this entity."),
    field("ForceReconcile", "Force Reconcile", SystemTypes.UIFieldType.CheckBox, [], false, false, 99, "Configuration|1", "Run reconciliation on every load. Use carefully for large data sets because it can slow processing."),
    field("DestinationState", "Destination State", SystemTypes.UIFieldType.SelectList, DESTINATION_STATE_OPTIONS, false, true, 1, "Status|2", "Current processing state for this entity deployment."),
    field("LastSyncUpdateUpdateDate", "Updated Date Cursor", SystemTypes.UIFieldType.DateTime, [], false, false, 2, "Status|2", "Cursor date used to pick up records updated after this value."),
    field("LastSyncUpdateIdentifierValue", "Identifier Cursor", SystemTypes.UIFieldType.TextBox, [], false, false, 3, "Status|2", "Cursor identifier used to pick up records after this value. The updated date cursor takes priority when both are set."),
    field("LastSyncStartDate", "Last Sync Start Date", SystemTypes.UIFieldType.Label, [], false, false, 4, "Status|2", "Most recent sync start date for this entity deployment."),
    field("LastSyncEndDate", "Last Sync End Date", SystemTypes.UIFieldType.Label, [], false, true, 5, "Status|2", "Most recent sync end date for this entity deployment."),
    field("LastErrors", "Last Error", SystemTypes.UIFieldType.Label, [], false, false, 6, "Status|2", "Most recent sync errors for this entity deployment.")
  ];

  return {
    ...(base || metadata || {}),
    Fields: resolvedFields
  };
};

export const getDestinationDeploymentRows = (destination, defaults = {}) =>
  asArray(readValue(
    destination,
    getDeploymentCollectionField(defaults),
    lowerFirst(getDeploymentCollectionField(defaults)),
    "Deployments",
    "deployments"
  ));

export const getEntityDeploymentRows = (destination) =>
  asArray(readValue(destination, "EntityObjectDeployments", "entityObjectDeployments"));

export const getDestinationRowKey = (row, index = 0) =>
  readValue(row, "_tempKey", "UniqueId", "uniqueId", "DeploymentId", "deploymentId", "_id", "id")
  || `${readValue(row, "SystemName", "systemName", "Name", "name", "EntityDisplayName", "entityDisplayName") || "row"}-${index}`;

export const getDeploymentRecordId = (deployment) =>
  readValue(deployment, "UniqueId", "uniqueId", "DeploymentId", "deploymentId", "_id", "id") || "";

const CONNECTION_CHECK_FIELD_GROUPS = {
  Status: ["DestinationConnectionCheckStatus", "ConnectionCheckStatus"],
  Message: ["DestinationConnectionCheckMessage", "ConnectionCheckMessage"],
  CheckedUtc: ["DestinationConnectionCheckedUtc", "ConnectionCheckedUtc"],
  DeploymentId: ["DestinationConnectionCheckDeploymentId", "ConnectionCheckDeploymentId"],
  DeploymentName: ["DestinationConnectionCheckDeploymentName", "ConnectionCheckDeploymentName"]
};

const readExplicitConnectionCheckValue = (source, fieldNames = []) => {
  const fieldName = fieldNames.find((name) => hasOwn(source, name));
  return fieldName ? readValue(source, fieldName) ?? "" : undefined;
};

const readSavedConnectionCheckValue = (source, fieldNames = []) => {
  const value = readValue(source, ...fieldNames);
  return value === undefined || value === null || value === "" ? undefined : value;
};

const resolveConnectionCheckValue = ({ currentValues = {}, destination, deployments = [] }, fieldNames = []) => {
  const currentValue = readExplicitConnectionCheckValue(currentValues, fieldNames);
  if (currentValue !== undefined) return currentValue;

  const destinationValue = readSavedConnectionCheckValue(destination, fieldNames);
  if (destinationValue !== undefined) return destinationValue;

  const deployment = deployments.find((row) => readSavedConnectionCheckValue(row, fieldNames) !== undefined);
  const deploymentValue = readSavedConnectionCheckValue(deployment, fieldNames);
  return deploymentValue ?? "";
};

const getDeploymentCheckIdentityValues = (deployment, index = 0) => ([
  getDeploymentRecordId(deployment),
  getDestinationRowKey(deployment, index),
  readValue(deployment, "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId"),
  readValue(deployment, "WarehouseDeploymentId", "warehouseDeploymentId"),
  readValue(deployment, "FileStorageDeploymentId", "fileStorageDeploymentId"),
  readValue(deployment, "ExternalSystemDeploymentId", "externalSystemDeploymentId"),
  readValue(deployment, ...CONNECTION_CHECK_FIELD_GROUPS.DeploymentId)
].filter((value) => value !== undefined && value !== null && value !== ""));

const normalizeConnectionCheckDeploymentId = ({ status, deploymentId, deployments = [] }) => {
  const statusText = String(status || "").trim().toLowerCase();
  const deploymentIdText = String(deploymentId || "").trim();
  if (statusText !== "success") return deploymentIdText;

  const matchedDeployment = deployments.find((row, index) =>
    getDeploymentCheckIdentityValues(row, index).some((value) => String(value) === deploymentIdText)
  );
  if (matchedDeployment) return getDeploymentRecordId(matchedDeployment) || deploymentIdText;

  if (!deploymentIdText && deployments.length === 1) return getDeploymentRecordId(deployments[0]) || "";
  return deploymentIdText;
};

const buildConnectionCheckStepValues = ({ currentValues = {}, destination, deployments = [] }) => {
  const status = resolveConnectionCheckValue({ currentValues, destination, deployments }, CONNECTION_CHECK_FIELD_GROUPS.Status);
  const deploymentId = normalizeConnectionCheckDeploymentId({
    status,
    deploymentId: resolveConnectionCheckValue({ currentValues, destination, deployments }, CONNECTION_CHECK_FIELD_GROUPS.DeploymentId),
    deployments
  });
  return {
    DestinationConnectionCheckStatus: status || "",
    DestinationConnectionCheckMessage: resolveConnectionCheckValue({ currentValues, destination, deployments }, CONNECTION_CHECK_FIELD_GROUPS.Message) || "",
    DestinationConnectionCheckedUtc: resolveConnectionCheckValue({ currentValues, destination, deployments }, CONNECTION_CHECK_FIELD_GROUPS.CheckedUtc) || "",
    DestinationConnectionCheckDeploymentId: deploymentId || "",
    DestinationConnectionCheckDeploymentName: resolveConnectionCheckValue({ currentValues, destination, deployments }, CONNECTION_CHECK_FIELD_GROUPS.DeploymentName) || ""
  };
};

export const dedupeRowsByRecordId = (rows = [], getRecordId = getDeploymentRecordId) => {
  const seen = new Set();
  return asArray(rows).filter((row) => {
    const recordId = String(getRecordId(row) || "").trim().toLowerCase();
    if (!recordId) return true;
    if (seen.has(recordId)) return false;
    seen.add(recordId);
    return true;
  });
};

const getDestinationDeploymentCollectionFieldNames = (defaults = {}) => [
  getDeploymentCollectionField(defaults),
  lowerFirst(getDeploymentCollectionField(defaults)),
  "Deployments",
  "deployments",
  "APIDeployments",
  "apiDeployments",
  "WarehouseDeployments",
  "warehouseDeployments",
  "StorageDeployments",
  "storageDeployments",
  "StorageDeploymentsAlias",
  "storageDeploymentsAlias",
  "ExternalSystemDeployments",
  "externalSystemDeployments"
].filter(Boolean);

const normalizeDeploymentRowsForSave = (rows = []) => dedupeRowsByRecordId(rows).map((row) => {
  const { _tempKey, ...cleanRow } = row || {};
  return {
    ...cleanRow,
    IsNew: false
  };
});

export const setDestinationDeploymentRows = (destination, defaults = {}, rows = []) => {
  const distinctRows = normalizeDeploymentRowsForSave(rows);
  return {
    ...(destination || {}),
    [getDeploymentCollectionField(defaults)]: distinctRows,
    Deployments: distinctRows
  };
};

export const buildDestinationDeploymentSavePayload = (destination, defaults = {}, rows = []) => {
  const next = { ...(destination || {}) };
  getDestinationDeploymentCollectionFieldNames(defaults).forEach((fieldName) => {
    delete next[fieldName];
  });
  return {
    ...next,
    Deployments: normalizeDeploymentRowsForSave(rows)
  };
};

export const setEntityDeploymentRows = (destination, rows = []) => ({
  ...(destination || {}),
  EntityObjectDeployments: dedupeRowsByRecordId(rows, (row) => readValue(row, "UniqueId", "uniqueId", "_id", "id"))
});

export const resetDestinationDownstreamCollections = (destination, defaults = {}) => {
  const next = { ...(destination || {}) };
  [
    ...getDestinationDeploymentCollectionFieldNames(defaults),
    "EntityObjectDeployments",
    "entityObjectDeployments"
  ].forEach((fieldName) => {
    if (fieldName) next[fieldName] = [];
  });
  return next;
};

export const resetDestinationDownstreamStepValues = (values = {}) => {
  const next = {
    ...(values || {}),
    DeploymentCount: 0,
    DeploymentIds: [],
    EntityDeploymentCount: 0
  };
  DESTINATION_DOWNSTREAM_STATUS_FIELD_NAMES.forEach((fieldName) => {
    next[fieldName] = "";
  });
  return next;
};

export const buildDestinationConnectionCheckMetadata = ({ destination, deployment, defaults = {} }) => {
  const destinationId = getId(destination);
  const deploymentId = getDeploymentRecordId(deployment);
  const metadata = {
    id: destinationId,
    DestinationId: destinationId,
    DeploymentId: deploymentId,
    SchemaDestinationDeploymentId: deploymentId
  };

  switch (getDestinationKind(defaults)) {
    case DESTINATION_KIND_WAREHOUSE:
      return {
        ...metadata,
        wdid: deploymentId,
        WarehouseDeploymentId: deploymentId
      };
    case DESTINATION_KIND_FILE_STORAGE:
      return {
        ...metadata,
        fsdid: deploymentId,
        wdid: deploymentId,
        FileStorageDeploymentId: deploymentId
      };
    case DESTINATION_KIND_EXTERNAL_SYSTEM:
      return {
        ...metadata,
        esdid: deploymentId,
        ExternalSystemDeploymentId: deploymentId
      };
    case DESTINATION_KIND_SCHEMA_API:
      return {
        ...metadata,
        DeploymentId: deploymentId,
        SchemaDestinationDeploymentId: deploymentId,
        APIEndpointDeploymentId: deploymentId
      };
    default:
      return metadata;
  }
};

const normalizeDeploymentTargetDisplayName = (value) => {
  const text = String(value || "").trim();
  return text === "/" ? "Root" : value;
};

const isRouteSafe = (value) => /^[A-Za-z0-9_-]+$/.test(String(value || ""));

const getRouteSafeValue = (...values) => asArray(values)
  .map((value) => String(value || "").trim())
  .find((value) => isRouteSafe(value)) || "";

const trimTrailingSlash = (value) => String(value || "").replace(/\/+$/, "");

const getConfiguredEndpointApiBaseUri = () => {
  const convokeApiUri = window?.configData?.API_URIS?.CONVOKE || "";
  const apiIndex = convokeApiUri.toLowerCase().lastIndexOf("/api/");
  if (apiIndex >= 0) return trimTrailingSlash(convokeApiUri.slice(0, apiIndex));
  return trimTrailingSlash(convokeApiUri);
};

const buildAbsoluteEndpointUrl = (path, deployment) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const deploymentApiBaseUri = deployment ? readValue(deployment, "APIBaseURI", "apiBaseURI", "ApiBaseUri", "apiBaseUri") || "" : "";
  const baseUri = trimTrailingSlash(deploymentApiBaseUri) || getConfiguredEndpointApiBaseUri();
  const normalizedPath = String(path).startsWith("/") ? String(path) : `/${path}`;
  return baseUri ? `${baseUri}${normalizedPath}` : normalizedPath;
};

const normalizeMcpConnectionUrl = (url, deployment) => {
  const absoluteUrl = buildAbsoluteEndpointUrl(url, deployment);
  if (!absoluteUrl) return absoluteUrl;
  return absoluteUrl.replace(/\/test-connect(?=([?#]|$))/i, "");
};

const getEndpointClientShortName = (destination, deployment) => getRouteSafeValue(
  readValue(destination, "ClientShortName", "clientShortName"),
  readValue(deployment, "ClientShortName", "clientShortName")
);

const getCoreProfileClientShortName = (coreContext) => getRouteSafeValue(
  readValue(coreContext?.Profile, "ClientShortName", "clientShortName")
);

const getEndpointClientId = (destination, deployment) =>
  readValue(destination, "ClientId", "clientId")
  || readValue(deployment, "ClientId", "clientId")
  || "";

export const resolveEndpointClientShortNameAsync = async ({ destination, deployment, clientMgr } = {}) => {
  const direct = getEndpointClientShortName(destination, deployment);
  if (direct) return direct;

  const clientId = getEndpointClientId(destination, deployment);
  if (!clientId || !clientMgr) return "";

  try {
    const result = clientMgr.getItem
      ? await clientMgr.getItem(clientId)
      : await clientMgr.Query?.({ model: "x => x.IsDeleted == false && x._id == clientId", params: { clientId } });
    const client = getOperationItem(result) || getOperationItems(result)[0] || null;
    return getRouteSafeValue(
      readValue(client, "ClientShortName", "clientShortName", "ShortName", "shortName", "SystemName", "systemName", "Name", "name")
    );
  } catch {
    return "";
  }
};

export const applyEndpointClientRouteContext = (destination, { defaults = {}, coreContext } = {}) => {
  if (!destination || !isSchemaAPISetup(defaults)) return destination;
  const clientShortName = getEndpointClientShortName(destination, null) || getCoreProfileClientShortName(coreContext);
  return clientShortName
    ? { ...destination, ClientShortName: clientShortName }
    : destination;
};

const isMcpSurfaceEnabled = (publicSurfaceMode, deployment = {}) => {
  const mode = Number(publicSurfaceMode);
  if (mode === 2 || mode === 3) return true;
  const mcpEnabled = readValue(deployment, "McpEnabled", "mcpEnabled");
  return mcpEnabled === true || String(mcpEnabled).trim().toLowerCase() === "true";
};

const getEndpointDeploymentName = (destination, routeKey) => {
  const destinationName = readValue(destination, "Name", "name") || "";
  if (destinationName && routeKey && routeKey !== destinationName) return `${destinationName} - ${routeKey}`;
  return destinationName || routeKey || "";
};

const buildEndpointMcpConnectorUrl = ({ destination, deployment, routeKey, publicSurfaceMode }) => {
  if (!isMcpSurfaceEnabled(publicSurfaceMode, deployment) || !isRouteSafe(routeKey)) return "";
  const clientShortName = getEndpointClientShortName(destination, deployment);
  return clientShortName ? normalizeMcpConnectionUrl(`/mcp/${clientShortName}/V1/${routeKey}`, deployment) : "";
};

export const getDeploymentDisplayName = (deployment, defaults = {}) =>
  normalizeDeploymentTargetDisplayName(readValue(
    deployment,
    "DeploymentName",
    "deploymentName",
    getDeploymentTargetFieldName(defaults),
    getDeploymentTargetFieldName(defaults).charAt(0).toLowerCase() + getDeploymentTargetFieldName(defaults).slice(1),
    "SystemName",
    "systemName",
    "SchemaDestinationName",
    "schemaDestinationName"
  ) || getDeploymentRecordId(deployment));

export const getDeploymentCardTitle = (deployment, defaults = {}) => {
  const explicitName = readValue(deployment, "DeploymentName", "deploymentName", "Text", "text");
  const version = readValue(deployment, "CurrentVersion", "currentVersion", "CurrentSchemaVersion", "currentSchemaVersion");
  if (explicitName) return version ? `${explicitName} v${version}` : explicitName;
  const destinationName = readValue(deployment, "SchemaDestinationName", "schemaDestinationName");
  const targetName = getDeploymentDisplayName(deployment, defaults);
  if (destinationName && targetName && targetName !== destinationName) {
    return `${destinationName} - ${targetName}${version ? ` v${version}` : ""}`;
  }
  return targetName || destinationName || getDeploymentLabel(defaults);
};

export const getEntityObjectRecordId = (entityObject) =>
  readValue(entityObject, "EntityObjectId", "entityObjectId", "UniqueId", "uniqueId") || getId(entityObject) || "";

export const getEntityObjectDisplayName = (entityObject) =>
  readValue(entityObject, "DisplayName", "displayName", "ObjectName", "objectName", "SystemName", "systemName", "Name", "name")
  || getEntityObjectRecordId(entityObject);

export const getSchemaRecordId = (schema) =>
  readValue(schema, "SchemaId", "schemaId", "UniqueId", "uniqueId") || getId(schema) || "";

export const getDeploymentOptions = (deployments, defaults = {}) =>
  asArray(deployments)
    .filter((deployment) => readValue(deployment, "IsDeleted", "isDeleted") !== true)
    .map((deployment) => option(getDeploymentDisplayName(deployment, defaults), getDeploymentRecordId(deployment)))
    .filter((entry) => entry.Value);

export const getEntityObjectOptions = (entityObjects) =>
  asArray(entityObjects)
    .filter((entityObject) => readValue(entityObject, "IsDeleted", "isDeleted") !== true)
    .map((entityObject) => option(getEntityObjectDisplayName(entityObject), getEntityObjectRecordId(entityObject)))
    .filter((entry) => entry.Value);

export const normalizeDestinationFormChange = (previous, updatedItem, fieldName, value, defaults = {}) => {
  const parentTargetField = getParentTargetFieldName(defaults);
  const systemTypeFieldName = getDestinationSystemTypeFieldName(defaults);
  const systemTypeValue = getDestinationSystemTypeValue(defaults);
  const next = {
    ...(updatedItem || previous || {}),
    [fieldName]: value,
    DestinationSystemType: systemTypeValue,
    [systemTypeFieldName]: systemTypeValue,
    IsActive: true,
    IsDeleted: false
  };
  if (fieldName === parentTargetField) {
    next.SystemName = value || next.SystemName || "";
  }
  if (fieldName === "Name" && !next[parentTargetField]) {
    next[parentTargetField] = getDestinationDefaultTargetValue(value, defaults);
    next.SystemName = next[parentTargetField];
  }
  return keepObjectStateWhenEqual(previous, next);
};

export const normalizeDeploymentRow = (row, destination, defaults = {}) => {
  const systemTypeFieldName = getDestinationSystemTypeFieldName(defaults);
  const targetFieldName = getDeploymentTargetFieldName(defaults);
  const stateFieldName = getDeploymentStateFieldName(defaults);
  const destinationName = readValue(destination, "Name", "name") || getDestinationKind(defaults) || "Destination";
  const rawTargetValue = readValue(row, targetFieldName, targetFieldName.charAt(0).toLowerCase() + targetFieldName.slice(1), "SystemName", "systemName");
  const defaultConnectionString = readValue(destination, "DefaultConnectionString", "defaultConnectionString") || "";
  const rawConnectionString = readValue(row, "ConnectionString", "connectionString") || "";
  const defaultDeploymentValues = readValue(defaults, "defaultDeploymentValues", "DefaultDeploymentValues") || {};
  const schemaAPISetup = isSchemaAPISetup(defaults);
  const useInheritedConnectionString = readValue(row, "UseInheritedConnectionString", "useInheritedConnectionString") === true
    || !String(rawConnectionString).trim()
    || (!!defaultConnectionString && String(rawConnectionString) === String(defaultConnectionString));
  const targetValue = (String(rawTargetValue ?? "").trim()
    ? normalizeDestinationTargetValue(rawTargetValue, destinationName, defaults)
    : "")
    || resolveDestinationTargetValue(destination, destinationName, defaults)
    || "default";
  const publicSurfaceMode = readValue(row, "PublicSurfaceMode", "publicSurfaceMode") || readValue(defaultDeploymentValues, "PublicSurfaceMode", "publicSurfaceMode") || "";
  const hostingMode = readValue(row, "HostingMode", "hostingMode") || readValue(defaultDeploymentValues, "HostingMode", "hostingMode") || 1;
  const defaultApiAuthenticationMode = isMcpOnlyEndpointSetup(defaults)
    ? ENDPOINT_AUTH_MODE_OAUTH2_USER_FLOW
    : readValue(defaultDeploymentValues, "ApiAuthenticationMode", "apiAuthenticationMode") || ENDPOINT_AUTH_MODE_PAT_TOKEN;
  const apiAuthenticationMode = isMcpOnlyEndpointSetup(defaults)
    ? defaultApiAuthenticationMode
    : readValue(row, "ApiAuthenticationMode", "apiAuthenticationMode") || defaultApiAuthenticationMode;
  const defaultMcpExecutionIdentityMode = isMcpOnlyEndpointSetup(defaults)
    ? ENDPOINT_MCP_IDENTITY_MODE_USER_CONTEXT
    : readValue(defaultDeploymentValues, "McpExecutionIdentityMode", "mcpExecutionIdentityMode") || ENDPOINT_MCP_IDENTITY_MODE_SERVICE_ACCOUNT;
  const mcpExecutionIdentityMode = isMcpOnlyEndpointSetup(defaults)
    ? defaultMcpExecutionIdentityMode
    : readValue(row, "McpExecutionIdentityMode", "mcpExecutionIdentityMode") || defaultMcpExecutionIdentityMode;
  const endpointClientShortName = schemaAPISetup ? getEndpointClientShortName(destination, row) : "";
  const endpointDeploymentName = schemaAPISetup
    ? readValue(row, "DeploymentName", "deploymentName") || getEndpointDeploymentName(destination, targetValue)
    : "";
  const endpointMcpConnectorUrl = schemaAPISetup
    ? normalizeMcpConnectionUrl(readValue(row, "McpConnectorUrl", "mcpConnectorUrl"), row) || buildEndpointMcpConnectorUrl({
      destination,
      deployment: row,
      routeKey: targetValue,
      publicSurfaceMode
    })
    : "";
  return {
    ...defaultDeploymentValues,
    ...(row || {}),
    IsNew: Boolean(readValue(row, "_tempKey")) || !getDeploymentRecordId(row),
    IsActive: readValue(row, "IsActive", "isActive") !== false,
    IsDeleted: readValue(row, "IsDeleted", "isDeleted") === true,
    UniqueId: readValue(row, "UniqueId", "uniqueId") || createClientGuid(),
    SchemaDestinationName: readValue(destination, "Name", "name") || "Destination",
    CurrentSchemaVersionId: readValue(destination, "ActiveSchemaVersionId", "activeSchemaVersionId") || readValue(row, "CurrentSchemaVersionId", "currentSchemaVersionId") || "",
    [systemTypeFieldName]: getDestinationSystemTypeValue(defaults),
    SystemName: targetValue,
    [targetFieldName]: targetValue,
    ...(schemaAPISetup
      ? {
        PublicSurfaceMode: publicSurfaceMode,
        HostingMode: hostingMode,
        ApiAuthenticationMode: apiAuthenticationMode,
        ...(endpointClientShortName ? { ClientShortName: endpointClientShortName } : {}),
        ...(endpointDeploymentName ? { DeploymentName: endpointDeploymentName } : {}),
        ...(endpointMcpConnectorUrl ? { McpConnectorUrl: endpointMcpConnectorUrl } : {}),
        WarehouseNativeQueryMode: readValue(row, "WarehouseNativeQueryMode", "warehouseNativeQueryMode") ?? readValue(defaultDeploymentValues, "WarehouseNativeQueryMode", "warehouseNativeQueryMode") ?? 0,
        McpExecutionIdentityMode: mcpExecutionIdentityMode
      }
      : {
        UseInheritedConnectionString: useInheritedConnectionString,
        ConnectionString: useInheritedConnectionString ? "" : rawConnectionString,
        EnableSync: readValue(row, "EnableSync", "enableSync") === true
      }),
    ...(getDestinationKind(defaults) === DESTINATION_KIND_EXTERNAL_SYSTEM
      ? { EnablePreview: readValue(row, "EnablePreview", "enablePreview") !== false }
      : {}),
    ...(stateFieldName ? { [stateFieldName]: readValue(row, stateFieldName, stateFieldName.charAt(0).toLowerCase() + stateFieldName.slice(1)) || 0 } : {})
  };
};

export const createDeploymentDraft = (destination, defaults = {}, sequence = 1) => normalizeDeploymentRow({
  _tempKey: `destination-deployment-draft-${Date.now()}-${sequence}`,
  IsNew: true,
  IsActive: true,
  IsDeleted: false,
  UniqueId: createClientGuid()
}, destination, defaults);

const findOptionText = (options, value) =>
  asArray(options).find((entry) => String(readValue(entry, "Value", "value")) === String(value))?.Text || "";

export const normalizeEntityDeploymentRow = ({
    row,
    entityObjects,
    deployments,
    defaults = {}
}) => {
  const entityObjectId = readValue(row, "EntityObjectId", "entityObjectId") || "";
  const deploymentId = readValue(row, "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId") || "";
  const entityOptions = getEntityObjectOptions(entityObjects);
  const deploymentOptions = getDeploymentOptions(deployments, defaults);
  return {
    ...(row || {}),
    IsNew: Boolean(readValue(row, "_tempKey")) || !readValue(row, "UniqueId", "uniqueId", "_id", "id"),
    IsActive: readValue(row, "IsActive", "isActive") !== false,
    IsDeleted: readValue(row, "IsDeleted", "isDeleted") === true,
    UniqueId: readValue(row, "UniqueId", "uniqueId") || createClientGuid(),
    EntityObjectId: entityObjectId,
    EntityDisplayName: findOptionText(entityOptions, entityObjectId) || readValue(row, "EntityDisplayName", "entityDisplayName") || "Entity Object",
    SchemaDestinationDeploymentId: deploymentId,
    SchemaDestinationName: findOptionText(deploymentOptions, deploymentId) || readValue(row, "SchemaDestinationName", "schemaDestinationName") || "Deployment",
    DestinationState: readValue(row, "DestinationState", "destinationState") || 1,
    DisableTruncate: readValue(row, "DisableTruncate", "disableTruncate") !== false,
    ForceReconcile: readValue(row, "ForceReconcile", "forceReconcile") === true,
    FilePackagingMode: readValue(row, "FilePackagingMode", "filePackagingMode") || 1
  };
};

export const createEntityDeploymentDraft = ({ entityObjects, deployments, existingRows, defaults = {}, sequence = 1 }) => {
  const usedEntityIds = new Set(asArray(existingRows).map((row) => String(readValue(row, "EntityObjectId", "entityObjectId") || "")));
  const nextEntity = asArray(entityObjects).find((entityObject) => !usedEntityIds.has(String(getEntityObjectRecordId(entityObject))))
    || asArray(entityObjects)[0]
    || null;
  const deployment = asArray(deployments).find((row) => readValue(row, "IsActive", "isActive") !== false)
    || asArray(deployments)[0]
    || null;
  return normalizeEntityDeploymentRow({
    row: {
      _tempKey: `destination-entity-deployment-draft-${Date.now()}-${sequence}`,
      IsNew: true,
      IsActive: true,
      IsDeleted: false,
      UniqueId: createClientGuid(),
      EntityObjectId: nextEntity ? getEntityObjectRecordId(nextEntity) : "",
      SchemaDestinationDeploymentId: deployment ? getDeploymentRecordId(deployment) : ""
    },
    entityObjects,
    deployments,
    defaults
  });
};

export const validateDestinationDraft = ({ destination, fields, hasAndShowValidationErrors, groupId = DESTINATION_FORM_GROUP_ID }) => {
  if (!destination) throw new Error("Destination is not available.");
  if (typeof hasAndShowValidationErrors === "function" && hasAndShowValidationErrors(groupId)) {
    return "Complete the required destination fields before continuing.";
  }
  return validateRequiredIntegrationFields(destination, fields);
};

export const validateDeploymentRows = (rows, fields, label = "Deployment") => {
  if (!asArray(rows).filter((row) => readValue(row, "IsDeleted", "isDeleted") !== true).length) {
    return `Add at least one ${label}.`;
  }
  const requiredFields = asArray(fields).filter((field) => readValue(field, "IsRequired", "isRequired"));
  for (const row of asArray(rows)) {
    const missingField = requiredFields.find((field) => {
      const fieldName = readValue(field, "FieldName", "fieldName");
      if (String(fieldName || "").toLowerCase() === "connectionstring"
        && readValue(row, "UseInheritedConnectionString", "useInheritedConnectionString") === true) {
        return false;
      }
      const value = readValue(row, fieldName);
      if (value === undefined || value === null) return true;
      if (typeof value === "string") return value.trim() === "";
      if (Array.isArray(value)) return value.length === 0;
      return false;
    });
    if (missingField) {
      return `${label || "Deployment"} ${getDisplayName(row) || getId(row)} is missing ${readValue(missingField, "DisplayName", "displayName", "FieldName", "fieldName")}.`;
    }
  }
  return "";
};

export const validateEntityDeploymentRows = (rows, fields, label = "Entity Deployment") => {
  if (!asArray(rows).filter((row) => readValue(row, "IsDeleted", "isDeleted") !== true).length) {
    return "Select at least one Entity Object for this destination.";
  }
  return validateRequiredCardGridRows(rows, fields, label);
};

export const extractSavedItem = (saveResult, fallback) =>
  getOperationItem(saveResult)
  || getFirst(saveResult?.Items)
  || getFirst(saveResult?.items)
  || saveResult?.Item
  || saveResult?.item
  || fallback;

export const saveDestination = async ({ manager, destination, coreState, fallbackMessage }) => {
  if (!manager?.saveItem) throw new Error("Destination service is not available.");
  const result = await manager.saveItem(destination);
  requireSuccessfulServiceResponse(result, fallbackMessage || "Destination could not be saved.", coreState);
  const saved = extractSavedItem(result, destination);
  if (!getId(saved)) throw new Error("Destination save did not return a Destination id.");
  return saved;
};

export const loadDestinationById = async ({ manager, destinationId, coreState, fallbackMessage = "Destination could not be loaded." }) => {
  if (!destinationId) return null;
  if (manager?.getItem) {
    const result = await manager.getItem(destinationId);
    requireSuccessfulServiceResponse(result, fallbackMessage, coreState);
    return getOperationItem(result) || extractSavedItem(result, null);
  }
  if (manager?.Query) {
    const result = await manager.Query({
      model: "x => x.IsDeleted == false && (x._id == destinationId || x.UniqueId == destinationId)",
      params: { destinationId },
      id: destinationId
    });
    requireSuccessfulServiceResponse(result, fallbackMessage, coreState);
    return getOperationItems(result).find((item) =>
      getId(item) === destinationId
      || readValue(item, "UniqueId", "uniqueId") === destinationId
    ) || null;
  }
  throw new Error("Destination service is not available.");
};

export const getDestinationRecordId = (destination) =>
  getId(destination) || readValue(destination, "UniqueId", "uniqueId") || "";

export const getDestinationOptionText = (destination, defaults = {}) =>
  readValue(destination, "Name", "name", "DisplayName", "displayName", "SystemName", "systemName", getParentTargetFieldName(defaults))
  || getDestinationRecordId(destination);

export const getExistingDestinationOptions = (destinations, defaults = {}, schemaContext = {}) => {
  const schemaId = readValue(schemaContext, "schemaId", "SchemaId") || "";
  const systemTypeFieldName = getDestinationSystemTypeFieldName(defaults);
  const systemTypeValue = getDestinationSystemTypeValue(defaults);
  const hasSystemType = systemTypeValue !== undefined && systemTypeValue !== null && String(systemTypeValue) !== "";

  return asArray(destinations)
    .filter((destination) => readValue(destination, "IsDeleted", "isDeleted") !== true)
    .filter((destination) => !schemaId || String(readValue(destination, "SchemaId", "schemaId") || "") === String(schemaId))
    .filter((destination) => !hasSystemType || String(readValue(destination, systemTypeFieldName, lowerFirst(systemTypeFieldName), "DestinationSystemType", "destinationSystemType") || "") === String(systemTypeValue))
    .map((destination) => ({
      Text: getDestinationOptionText(destination, defaults),
      Value: getDestinationRecordId(destination)
    }))
    .filter((optionItem) => optionItem.Value);
};

export const loadExistingDestinationOptions = async ({
  manager,
  defaults = {},
  schemaContext = {},
  coreState,
  fallbackMessage = "Existing destinations could not be loaded."
} = {}) => {
  if (!manager?.Query) return [];
  const schemaId = readValue(schemaContext, "schemaId", "SchemaId") || "";
  const queryParts = ["x.IsDeleted == false"];
  const params = {};

  if (schemaId) {
    queryParts.push("x.SchemaId == schemaId");
    params.schemaId = schemaId;
  }

  const result = await manager.Query({
    model: `x => ${queryParts.join(" && ")}`,
    params
  });
  requireSuccessfulServiceResponse(result, fallbackMessage, coreState);
  return getExistingDestinationOptions(getOperationItems(result), defaults, schemaContext);
};

export const buildDestinationStepValues = ({ currentValues = {}, destination, defaults = {} }) =>
  resetDestinationDownstreamStepValues({
    ...currentValues,
    DestinationSelectionMode: readValue(currentValues, "DestinationSelectionMode", "destinationSelectionMode") || "New",
    DeliveryProductResourceKey: readValue(currentValues, "DeliveryProductResourceKey", "deliveryProductResourceKey")
      || readValue(defaults, "DeliveryProductResourceKey", "deliveryProductResourceKey", "selectedDeliveryProductResourceKey")
      || "",
    DestinationId: getId(destination),
    DestinationItemName: getDestinationItemName(defaults),
    DestinationRelationship: getDestinationRelationship(defaults),
    DestinationSystemTypeValue: getDestinationSystemTypeValue(defaults),
    DestinationSystemTypeKey: getDestinationSystemTypeKey(defaults),
    SchemaId: readValue(destination, "SchemaId", "schemaId") || "",
    ActiveSchemaVersionId: readValue(destination, "ActiveSchemaVersionId", "activeSchemaVersionId") || ""
  });

export const buildDeploymentStepValues = ({ currentValues = {}, destination, deployments = [] }) => {
  const activeDeployments = asArray(deployments).filter((row) => readValue(row, "IsDeleted", "isDeleted") !== true);
  const firstDeployment = activeDeployments[0] || null;
  const connectionCheckValues = buildConnectionCheckStepValues({
    currentValues,
    destination,
    deployments: activeDeployments
  });
  return {
    ...currentValues,
    DestinationId: getId(destination),
    DeploymentCount: activeDeployments.length,
    DeploymentIds: asArray(deployments).map(getDeploymentRecordId).filter(Boolean),
    PublicSurfaceMode: readValue(firstDeployment, "PublicSurfaceMode", "publicSurfaceMode") || readValue(currentValues, "PublicSurfaceMode", "publicSurfaceMode") || "",
    ...connectionCheckValues
  };
};

export const buildEntityDeploymentStepValues = ({ currentValues = {}, destination, entityDeployments = [] }) => ({
  ...currentValues,
  DestinationId: getId(destination),
  EntityDeploymentCount: asArray(entityDeployments).filter((row) => readValue(row, "IsDeleted", "isDeleted") !== true).length
});

export const sameDestinationState = keepObjectStateWhenEqual;
